import {
  createNavigatorFactory,
  StackRouter,
  StackActions,
  useNavigationBuilder,
  type DefaultNavigatorOptions,
  type EventMapBase,
  type NavigatorTypeBagBase,
  type NavigationProp,
  type ParamListBase,
  type StackActionHelpers,
  type StackNavigationState,
  type StackRouterOptions,
  type StaticConfig,
  type TypedNavigator
} from '@react-navigation/native';
import { type FC, useEffect } from 'react';

import { MEIN_ORT_FEED_ROUTE } from '@/features/mein-ort/components/meinOrtTypes';
import { MeinOrtScreen } from '@/features/mein-ort/screens/MeinOrtScreen';
import type {
  AppTabScreenProps,
  TabRoute,
  TabParam
} from '@/navigation/types/navigation';

export type MeinOrtFeedParam = {
  [MEIN_ORT_FEED_ROUTE]: TabParam[typeof TabRoute.Radio];
};

const FeedEntry: FC = () => <></>;

type HistoryProps = DefaultNavigatorOptions<
  ParamListBase,
  undefined,
  StackNavigationState<ParamListBase>,
  object,
  EventMapBase,
  NavigationProp<ParamListBase>
> &
  StackRouterOptions & {
    requestedSelection: TabParam[typeof TabRoute.Radio];
  };

const MeinOrtHistoryNavigator: FC<HistoryProps> = ({
  children,
  initialRouteName,
  requestedSelection
}) => {
  const { state, navigation, descriptors, NavigationContent } =
    useNavigationBuilder<
      StackNavigationState<ParamListBase>,
      StackRouterOptions,
      StackActionHelpers<ParamListBase>,
      object,
      EventMapBase
    >(StackRouter, { children, initialRouteName });
  const active = state.routes[state.index];
  const activeSelection =
    active.params as MeinOrtFeedParam[typeof MEIN_ORT_FEED_ROUTE];

  useEffect(() => {
    const currentState = navigation.getState();
    const current = currentState.routes[currentState.index];
    const currentSelection =
      current.params as MeinOrtFeedParam[typeof MEIN_ORT_FEED_ROUTE];
    if (
      requestedSelection &&
      (requestedSelection.channel !== currentSelection?.channel ||
        requestedSelection.tab !== currentSelection?.tab)
    ) {
      navigation.dispatch({
        ...StackActions.push(MEIN_ORT_FEED_ROUTE, requestedSelection),
        target: state.key
      });
    }
  }, [requestedSelection, navigation, state.key]);

  return (
    <NavigationContent>
      <MeinOrtScreen
        channel={activeSelection?.channel}
        tab={activeSelection?.tab}
        onVisit={(params) => {
          navigation.dispatch({
            ...StackActions.push(MEIN_ORT_FEED_ROUTE, params),
            target: state.key
          });
        }}
      />
      {descriptors[active.key].render()}
    </NavigationContent>
  );
};

type MeinOrtHistoryTypeBag<
  ParamList extends ParamListBase = ParamListBase,
  NavigatorID extends string | undefined = string | undefined
> = {
  ParamList: ParamList;
  NavigatorID: NavigatorID;
  State: StackNavigationState<ParamList>;
  ScreenOptions: object;
  EventMap: EventMapBase;
  NavigationList: {
    [RouteName in keyof ParamList]: NavigationProp<
      ParamList,
      RouteName,
      NavigatorID,
      StackNavigationState<ParamList>,
      object,
      EventMapBase
    > &
      StackActionHelpers<ParamList>;
  };
  Navigator: typeof MeinOrtHistoryNavigator;
};

const createMeinOrtHistory = <
  const ParamList extends ParamListBase,
  const NavigatorID extends string | undefined = undefined,
  const TypeBag extends NavigatorTypeBagBase = MeinOrtHistoryTypeBag<
    ParamList,
    NavigatorID
  >,
  const Config extends StaticConfig<TypeBag> = StaticConfig<TypeBag>
>(
  config?: Config
): TypedNavigator<TypeBag, Config> =>
  createNavigatorFactory(MeinOrtHistoryNavigator)(config) as TypedNavigator<
    TypeBag,
    Config
  >;

const History = createMeinOrtHistory<MeinOrtFeedParam>();

type Props = AppTabScreenProps<typeof TabRoute.Radio>;

export const MeinOrtRouting: FC<Props> = ({ route }) => (
  <History.Navigator requestedSelection={route.params}>
    <History.Screen
      name={MEIN_ORT_FEED_ROUTE}
      component={FeedEntry}
      initialParams={route.params}
    />
  </History.Navigator>
);
